import React from "react";
import { Box, Skeleton } from "@mui/material";
import ReactECharts from "echarts-for-react";
import useChartTheme, { mixToward, seriesGradient } from "../useChartTheme";
import type { VisualizationMeta } from "hooks/useNLQuery";

interface Props {
  rows: Record<string, unknown>[];
  columns: string[];
  visualization: VisualizationMeta;
  loading?: boolean;
}

const AgenticPieChart: React.FC<Props> = ({ rows, columns, visualization, loading }) => {
  const { baseTooltip, seriesColors, textPrimary, chartHeight, dark } = useChartTheme();

  if (loading || !rows.length) {
    return <Skeleton variant="rounded" height={chartHeight} sx={{ borderRadius: 2 }} />;
  }

  const nameCol = visualization.x_axis || columns.find((c) => c !== "patient_id") || columns[0];
  const valueCol = visualization.y_axis || columns.find((c) => c !== nameCol && c !== "patient_id") || columns[1];

  if (!nameCol || !valueCol) return null;

  const pieData = rows.map((r, i) => ({
    name: String(r[nameCol] ?? "Unknown"),
    value: Number(r[valueCol] ?? 0),
    itemStyle: {
      // The dashboard's own donut gradients toward a lighter version of each
      // slice; flat fills here made the NL result read as a different chart
      // library on the same page.
      color: dark
        ? seriesGradient(
            seriesColors[i % seriesColors.length],
            "vertical",
            mixToward(seriesColors[i % seriesColors.length], "#ffffff", 0.4),
          )
        : seriesColors[i % seriesColors.length],
    },
  }));

  const option = {
    tooltip: {
      ...baseTooltip,
      trigger: "item" as const,
      formatter: "{b}: {c} ({d}%)",
    },
    legend: {
      orient: "vertical" as const,
      right: "5%",
      top: "center",
      textStyle: { color: textPrimary, fontSize: 11 },
    },
    series: [
      {
        type: "pie" as const,
        radius: ["35%", "65%"],
        center: ["40%", "50%"],
        avoidLabelOverlap: true,
        itemStyle: { borderRadius: 6, borderColor: "transparent", borderWidth: 2 },
        label: { show: false },
        emphasis: {
          label: { show: true, fontWeight: "bold" as const, fontSize: 13 },
        },
        data: pieData,
      },
    ],
  };

  return (
    <Box sx={{ width: "100%", height: chartHeight }}>
      <ReactECharts option={option} style={{ width: "100%", height: "100%" }} opts={{ renderer: "canvas" }} />
    </Box>
  );
};

export default AgenticPieChart;
